import { Injectable, NgZone } from '@angular/core';
// import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/angular';
import { Dictionary } from 'lodash';
import LogRocket from 'logrocket';
import moment from 'moment';

import { environment } from 'src/environments/environment';

import { CommonConstant } from '../constants';
import {
  DeviceState,
  FacilityDetails,
  Operator,
  PosLocation
} from 'src/app/models';

export const LOG_ROCKET_CUSTOM_EVENTS = {
  Ticket: {
    Created: 'TicketCreated',
    Voided: 'TicketVoided',
    Cancelled: 'TicketCancelled',
    Closed: 'TicketClosed'
  },
  TicketItem: {
    Created: 'TicketItemCreated',
    Voided: 'TicketItemVoided'
  },
  Transaction: {
    Complete: 'TransactionComplete',
    Declined: 'TransactionDeclined',
    InProgress: 'TransactionInProgress',
    Retry: 'TransactionRetry'
  },
  Diner: {
    Assigned: 'DinerAssigned',
    Unassigned: 'DinerUnassigned',
    OptForMealPlan: 'DinerOptForMealPlan'
  }
};

@Injectable({
  providedIn: 'root'
})
export class LogRocketProvider {
  isInitiated = false;

  constructor(private readonly ngZone: NgZone) {}
  /** Login to LogRocket */
  private init() {
    let env = '';
    if (environment.MSPOS_ENVIRONMENT === 'dev') {
      env = `${CommonConstant.ENVIRONMENT.DEV}.`;
    } else if (environment.MSPOS_ENVIRONMENT === 'staging') {
      env = `${CommonConstant.ENVIRONMENT.STAGING}.`;
    }

    LogRocket.init('apeduw/mealsuite-pos-v3', {
      release: environment.MSPOS_APP_VERSION,
      dom: {
        baseHref: `https://${env}pos.mealsuite.com`
      }
    });

    this.isInitiated = true;
  }

  startNewSession() {
    // Check if LogRocket is initialized
    if (!this.isInitiated) {
      this.init();
    }
    LogRocket.startNewSession();
  }

  identifyUser(uid: string, userVars: Dictionary<any>) {
    LogRocket.identify(uid, userVars);
  }

  startSession(
    operator: Operator,
    device: DeviceState,
    location: PosLocation,
    facilityDetails: FacilityDetails
  ) {
    const operatorId = operator.operator_id;
    const sessionName = `${operator.display_name} - ${operatorId} | ${device.name} - ${device.key}`;
    const sessionEmail = `${location.name} @ ${facilityDetails.name}`;

    this.ngZone.runOutsideAngular(() => {
      if (facilityDetails.enable_session_recording) {
        this.startNewSession();
        this.identifyUser(`${sessionName} | ${sessionEmail}`, {
          name: sessionName,
          email: sessionEmail
        });
        this.log('Starting new session', {
          device,
          location,
          facilityDetails
        });
      }

      Sentry.configureScope((scope) => {
        scope.setUser({
          id: operatorId,
          username: sessionName,
          email: sessionEmail
        });
      });
    });
  }

  log(name: string, data?: any) {
    const dateTime = moment().format(
      CommonConstant.MS_DATE_TIME.DATE_TIME_FORMAT
    );
    if (environment.MSPOS_IS_DEPLOYED_BUILD) {
      LogRocket.log(name, dateTime, ' - Data: ', data);
    } else {
      console.log(name, dateTime, ' - Data: ', data);
    }
  }

  error(name: string, data?: any) {
    const dateTime = moment().format(
      CommonConstant.MS_DATE_TIME.DATE_TIME_FORMAT
    );
    if (environment.MSPOS_IS_DEPLOYED_BUILD) {
      LogRocket.error(name, dateTime, data ? { ...data } : '');
    }
  }

  //Data attribute should be an object
  track(event: string, data: any) {
    LogRocket.track(event, data);
  }
}
